import { useNavigate, useSearch } from "@tanstack/react-router";
import type { HeaderContext } from "@tanstack/react-table";
import { useDebounceCallback } from "usehooks-ts";

import { useQueryBuilderContext } from "@/contexts/query-builder-context";

type HeaderSearchInputProps<TData, TValue> = {
  header: HeaderContext<TData, TValue>;
  name?: string;
};

export const HeaderSearchInput = <TData, TValue>({
  header,
  name,
}: HeaderSearchInputProps<TData, TValue>) => {
  const { builder: filtersQueryBuilder } = useQueryBuilderContext();
  const search = useSearch({ strict: false }) as Record<string, string | number | undefined>;
  const navigate = useNavigate();

  const headerId = header.column.id;

  const filterValue = search?.[headerId] || "";

  const updateFilter = useDebounceCallback((text: string) => {
    const newSearch = { ...search };

    newSearch.page = 1;

    if (text === "") {
      delete newSearch[headerId];
      filtersQueryBuilder.removeFilter(headerId);
    } else {
      const encodedText = encodeURIComponent(text);
      newSearch[headerId] = encodedText;
      filtersQueryBuilder.filter(headerId, encodedText, true);
    }

    navigate({
      to: ".",
      search: newSearch,
    });
  }, 500);

  const inputId = `${headerId}-filter`;

  return (
    <div className="flex flex-col gap-1.5">
      <label
        className="text-xs font-semibold tracking-wider whitespace-nowrap uppercase"
        htmlFor={inputId}
      >
        {name}
      </label>

      <input
        className="block h-9 w-full min-w-20 rounded-lg border border-line-control bg-surface px-3 text-sm font-normal text-content normal-case shadow-raised transition-[color,box-shadow,border-color] duration-150 placeholder:text-content-subtle hover:border-content-subtle focus:outline-none focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-focus-ring/40 focus-visible:outline-none"
        defaultValue={filterValue}
        id={inputId}
        onChange={(e) => {
          updateFilter(e.target.value);
        }}
        type="search"
      />
    </div>
  );
};
